// SPDX-License-Identifier: AGPL-3.0-or-later

import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {boosterBadgeMonths, boosterBadgeTier, boosterBadgeUrl} from '@app/features/boost/utils/BoosterBadge';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import {mediaUrl} from '@app/features/messaging/utils/MessagingUrlUtils';
import {
	type PremiumPackageMonths,
	premiumBadgeUrl,
	premiumPackageBadgeUrl,
	premiumPackageMonths,
} from '@app/features/premium/utils/PremiumBadgeAsset';
import {getPremiumProductFullName} from '@app/features/premium/utils/PremiumUtils';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {handleExternalLinkClick} from '@app/features/ui/utils/NativeUtils';
import {inventoryBadgePresentation} from '@app/features/user/components/modals/tabs/my_profile_tab/BadgeInventoryBadge';
import {BadgeInventoryContext} from '@app/features/user/components/modals/tabs/my_profile_tab/BadgeInventoryState';
import {sortByBadgeOrder} from '@app/features/user/components/modals/tabs/my_profile_tab/BadgeOrder';
import {
	botDeveloperBadgeUrl,
	botDeveloperTier,
	botDeveloperTierName,
	staffBadgeUrl,
	staffTierName,
} from '@app/features/user/components/modals/tabs/my_profile_tab/BadgeTiers';
import styles from '@app/features/user/components/popouts/UserProfileBadges.module.css';
import type {Profile} from '@app/features/user/models/Profile';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import {PublicUserFlags, UserPremiumTypes} from '@fluxer/constants/src/UserConstants';
import {getFormattedShortDate} from '@fluxer/date_utils/src/DateFormatting';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useContext, useMemo} from 'react';

const BADGE_ASSET_VERSION = '3';
// The capsule shows up to this many badges; past it, one fewer and a +N that opens the badge window.
const CAPSULE_SLOTS = 9;
const PROFILE_BADGE_PRIORITY: Record<string, number> = {
	staff: 0,
	premium: 1,
	premium_package: 1,
	booster: 1,
	bug_hunter: 3,
	bot_developer: 3,
};

// Micup: the flag badge icons are Micup's own, served under /badges/micup-*.
const badgeAssetUrl = (fileName: string) =>
	`${RuntimeConfig.staticCdnEndpoint}/badges/micup-${fileName}?v=${BADGE_ASSET_VERSION}`;

const STAFF_DESCRIPTOR = msg({
	message: '{productName} Staff',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const STAFF_TIER_DESCRIPTOR = msg({
	message: '{productName} Staff · {tier}',
	comment:
		'Badge title of a staff badge with a tier in the user profile badges popout. Preserve {productName} and {tier}; the tier name an admin set, inserted by code.',
});
const PARTNER_DESCRIPTOR = msg({
	message: '{productName} Partner',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const BUG_HUNTER_DESCRIPTOR = msg({
	message: '{productName} Bug Hunter',
	comment:
		'Short badge title in the user profile badges popout. Preserve {productName}; it is inserted by code. English locales use Title Case for official badge titles; other locales should use natural local capitalization.',
});
const PACKAGE_BADGE_COMMENT =
	'Tooltip of the early supporter badge in the user profile badges popout: given for good to early supporters, by the largest paid package they bought (1, 3 or 12 months). Keep it short.';
const PACKAGE_BADGE_DESCRIPTORS = {
	0: msg({message: 'Early Supporter', comment: PACKAGE_BADGE_COMMENT}),
	1: msg({message: 'Early Supporter · 1 month', comment: PACKAGE_BADGE_COMMENT}),
	3: msg({message: 'Early Supporter · 3 months', comment: PACKAGE_BADGE_COMMENT}),
	12: msg({message: 'Early Supporter · 12 months', comment: PACKAGE_BADGE_COMMENT}),
} as const;
const BOOSTER_NEW_DESCRIPTOR = msg({
	message: 'Server booster since {date}',
	comment:
		'Tooltip of the server booster badge in the user profile badges popout, in the first month of boosting a community without a break. Preserve {date}; a short date inserted by code.',
});
const BOOSTER_MONTHS_DESCRIPTOR = msg({
	message:
		'{months, plural, one {Server booster for # month · since {date}} other {Server booster for # months · since {date}}}',
	comment:
		'Tooltip of the server booster badge in the user profile badges popout: whole months of boosting a community without a break. Preserve {months} and {date}; a short date inserted by code.',
});

function boosterBadge(since: Date, i18n: I18n): IconBadge {
	const months = boosterBadgeMonths(since, new Date());
	const date = getFormattedShortDate(since, i18n.locale);
	return {
		type: 'icon',
		key: 'booster',
		iconUrl: boosterBadgeUrl(boosterBadgeTier(months)),
		tooltip: months === 0 ? i18n._(BOOSTER_NEW_DESCRIPTOR, {date}) : i18n._(BOOSTER_MONTHS_DESCRIPTOR, {months, date}),
	};
}

const MORE_BADGES_DESCRIPTOR = msg({
	message: '{count, plural, one {# more badge} other {# more badges}}',
	comment:
		'Accessible label and tooltip of the +N button at the end of the badge capsule on a profile; it opens the badge window.',
});

const VISIONARY_ID_DESCRIPTOR = msg({
	message: 'Visionary ID {visionaryIdLabel}',
	comment:
		'Short label in the user profile badges popout. Keep it concise. Preserve {visionaryIdLabel}; it is inserted by code.',
});

interface BaseBadge {
	key: string;
	priority?: number;
	tooltip: string;
	url?: string;
}

interface IconBadge extends BaseBadge {
	type: 'icon';
	iconUrl: string;
}

interface TextBadge extends BaseBadge {
	type: 'text';
	text: string;
}

export type ProfileBadge = IconBadge | TextBadge;
type Badge = ProfileBadge;

interface UserProfileBadgesProps {
	user: User;
	profile: Profile | null;
	isModal?: boolean;
	isMobile?: boolean;
	// The You page and DM welcome keep their existing crown next to the name.
	showPremium?: boolean;
	largeProfile?: boolean;
}

// The badges a profile shows, in order: the capsule and the badge window draw this same list.
export function useProfileBadges(user: User, profile: Profile | null, showPremium = true): Array<ProfileBadge> {
	const {i18n} = useLingui();
	const inventoryContext = useContext(BadgeInventoryContext);
	const inventory =
		inventoryContext && inventoryContext.userId === user.id && inventoryContext.loaded ? inventoryContext : null;
	const packageOverride: PremiumPackageMonths | null =
		Users.currentUser?.id === user.id ? (DeveloperOptions.premiumPackageBadgeOverride ?? null) : null;
	const packageMonths = packageOverride ?? premiumPackageMonths(user.flags);
	const badges = useMemo(() => {
		const result: Array<Badge> = [];
		// Micup: every flag badge shows on this self-hosted instance, without links to Fluxer's marketing pages.
		if (user.flags & PublicUserFlags.STAFF) {
			const staffTier = profile?.staffTier ?? null;
			const tier = staffTier ? staffTierName(staffTier, i18n) : null;
			result.push({
				type: 'icon',
				key: 'staff',
				iconUrl: staffBadgeUrl(staffTier),
				tooltip: tier
					? i18n._(STAFF_TIER_DESCRIPTOR, {productName: PRODUCT_NAME, tier})
					: i18n._(STAFF_DESCRIPTOR, {productName: PRODUCT_NAME}),
			});
		}
		if (showPremium && user.flags & PublicUserFlags.PREMIUM_BADGE) {
			result.push({
				type: 'icon',
				key: 'premium',
				iconUrl: premiumBadgeUrl(),
				tooltip: getPremiumProductFullName(),
			});
		}
		// Micup: the badge of the largest paid package; the server keeps its bit after Plus ends.
		if (packageMonths !== null) {
			result.push({
				type: 'icon',
				key: 'premium_package',
				iconUrl: premiumPackageBadgeUrl(packageMonths),
				tooltip: i18n._(PACKAGE_BADGE_DESCRIPTORS[packageMonths]),
			});
		}
		if (profile?.premiumGuildSince) {
			result.push(boosterBadge(profile.premiumGuildSince, i18n));
		}
		if (user.flags & PublicUserFlags.PARTNER) {
			result.push({
				type: 'icon',
				key: 'partner',
				iconUrl: badgeAssetUrl('partner.svg'),
				tooltip: i18n._(PARTNER_DESCRIPTOR, {productName: PRODUCT_NAME}),
			});
		}
		if (profile?.botDeveloperGuilds != null) {
			result.push({
				type: 'icon',
				key: 'bot_developer',
				iconUrl: botDeveloperBadgeUrl(botDeveloperTier(profile.botDeveloperGuilds)),
				tooltip: botDeveloperTierName(profile.botDeveloperGuilds, i18n),
			});
		}
		if (user.flags & PublicUserFlags.BUG_HUNTER) {
			result.push({
				type: 'icon',
				key: 'bug_hunter',
				iconUrl: badgeAssetUrl('bug-hunter-v2.webp'),
				tooltip: i18n._(BUG_HUNTER_DESCRIPTOR, {productName: PRODUCT_NAME}),
			});
		}
		if (profile?.premiumType === UserPremiumTypes.LIFETIME && profile.premiumLifetimeSequence != null) {
			const visionaryIdLabel = `#${profile.premiumLifetimeSequence}`;
			result.push({
				type: 'text',
				key: 'premium_sequence',
				text: visionaryIdLabel,
				tooltip: i18n._(VISIONARY_ID_DESCRIPTOR, {visionaryIdLabel}),
			});
		}
		// Micup: catalog badges from the profile, in the server's order; the description is plain text.
		for (const badge of profile?.badges ?? []) {
			result.push({
				type: 'icon',
				key: `badge-${badge.id}`,
				priority: 2,
				iconUrl: mediaUrl(`branding/badge-${badge.id}/${badge.icon}.webp?size=128`),
				tooltip: badge.description,
			});
		}
		return showPremium
			? result.sort(
					(a, b) =>
						(PROFILE_BADGE_PRIORITY[a.key] ?? a.priority ?? 4) - (PROFILE_BADGE_PRIORITY[b.key] ?? b.priority ?? 4),
				)
			: result;
	}, [
		user.flags,
		profile?.premiumType,
		profile?.premiumLifetimeSequence,
		profile?.premiumGuildSince,
		profile?.staffTier,
		profile?.botDeveloperGuilds,
		profile?.badges,
		i18n.locale,
		showPremium,
		packageMonths,
	]);
	let displayedBadges = badges;
	if (inventory) {
		const builtInKeys = new Set<string>(inventory.badges.map((badge) => badge.key));
		const catalogKeys = new Set(inventory.catalog.map((badge) => `badge-${badge.badge_id}`));
		displayedBadges = badges.filter((badge) => !builtInKeys.has(badge.key) && !catalogKeys.has(badge.key));
		for (const badge of [...inventory.badges, ...inventory.catalog]) {
			if (!badge.visible || (badge.source !== 'catalog' && badge.key === 'premium' && !showPremium)) continue;
			if (
				badge.source !== 'catalog' &&
				badge.key === 'premium_sequence' &&
				profile?.premiumType !== UserPremiumTypes.LIFETIME
			)
				continue;
			const key = badge.source === 'catalog' ? `badge-${badge.badge_id}` : badge.key;
			const existing = badges.find((entry) => entry.key === key);
			const presentation = inventoryBadgePresentation(badge, user, i18n);
			displayedBadges.push(
				existing ??
					(presentation.iconUrl
						? {
								type: 'icon',
								key,
								iconUrl: presentation.iconUrl,
								tooltip: presentation.name,
							}
						: {type: 'text', key, text: presentation.text ?? presentation.name, tooltip: presentation.name}),
			);
		}
		displayedBadges.sort(
			(a, b) => (PROFILE_BADGE_PRIORITY[a.key] ?? a.priority ?? 4) - (PROFILE_BADGE_PRIORITY[b.key] ?? b.priority ?? 4),
		);
	}
	const order = inventory ? inventory.order : profile?.badgeOrder;
	if (order?.length && RuntimeConfig.features.badge_inventory_enabled === true) {
		displayedBadges = sortByBadgeOrder(displayedBadges, (badge) => badge.key, order);
	}
	return displayedBadges;
}

export const UserProfileBadges: React.FC<UserProfileBadgesProps> = observer(
	({user, profile, isModal = false, isMobile = false, showPremium = true, largeProfile = false}) => {
		const {i18n} = useLingui();
		const displayedBadges = useProfileBadges(user, profile, showPremium);
		if (displayedBadges.length === 0) {
			return null;
		}
		const containerClassName = isModal
			? clsx(styles.containerModal, isMobile ? styles.containerModalMobile : styles.containerModalDesktop)
			: styles.containerPopout;
		const badgeClassName = showPremium ? styles.badge : isModal && isMobile ? styles.badgeMobile : styles.badgeDesktop;
		const isDesktopInteractions = !isMobile;
		// Profile surfaces draw the badges in a capsule that opens the badge window; the You page and DM welcome stay plain.
		const capsule = showPremium;
		const shownBadges =
			capsule && displayedBadges.length > CAPSULE_SLOTS ? displayedBadges.slice(0, CAPSULE_SLOTS - 1) : displayedBadges;
		const hiddenCount = displayedBadges.length - shownBadges.length;
		const openBadge = (event: React.MouseEvent, selectedKey: string) => {
			event.stopPropagation();
			// Loaded on the first click: the window stays out of every profile surface until it is opened.
			void import('@app/features/user/components/modals/profile_badge_modal/ProfileBadgeModal').then((module) =>
				module.openProfileBadgeModal({user, profile, badges: displayedBadges, selectedKey}),
			);
		};
		const renderInteractiveWrapper = (url: string | undefined, children: React.ReactNode) => {
			if (url && isDesktopInteractions) {
				return (
					<a
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						className={styles.link}
						onClick={(event) => handleExternalLinkClick(event, url)}
						data-flx="user.user-profile-badges.render-interactive-wrapper.link"
					>
						{children}
					</a>
				);
			}
			return (
				<div className={styles.link} data-flx="user.user-profile-badges.render-interactive-wrapper.link--2">
					{children}
				</div>
			);
		};
		return (
			<div
				className={clsx(containerClassName, largeProfile && styles.largeProfile, capsule && styles.capsule)}
				style={largeProfile ? ({'--profile-badge-height': '19px'} as React.CSSProperties) : undefined}
				data-flx="user.user-profile-badges.div"
			>
				{shownBadges.map((badge) => {
					const sequenceClassName = isModal && isMobile ? styles.sequenceBadgeMobile : styles.sequenceBadgeDesktop;
					const badgeContent =
						badge.type === 'icon' ? (
							<img
								src={badge.iconUrl}
								alt={badge.tooltip}
								className={badgeClassName}
								data-flx="user.user-profile-badges.img"
							/>
						) : (
							<span
								className={clsx(styles.sequenceBadge, sequenceClassName)}
								aria-hidden="true"
								data-flx="user.user-profile-badges.sequence-badge"
							>
								{badge.text}
							</span>
						);
					return (
						<Tooltip key={badge.key} text={badge.tooltip} maxWidth="xl" data-flx="user.user-profile-badges.tooltip">
							<FocusRing offset={-2} data-flx="user.user-profile-badges.focus-ring">
								{capsule ? (
									<button
										type="button"
										className={styles.capsuleButton}
										aria-label={badge.tooltip}
										onClick={(event) => openBadge(event, badge.key)}
										data-flx="user.user-profile-badges.open"
									>
										{badgeContent}
									</button>
								) : (
									renderInteractiveWrapper(badge.url, badgeContent)
								)}
							</FocusRing>
						</Tooltip>
					);
				})}
				{hiddenCount > 0 && (
					<Tooltip
						text={i18n._(MORE_BADGES_DESCRIPTOR, {count: hiddenCount})}
						maxWidth="xl"
						data-flx="user.user-profile-badges.more-tooltip"
					>
						<FocusRing offset={-2} data-flx="user.user-profile-badges.more-focus-ring">
							<button
								type="button"
								className={styles.more}
								aria-label={i18n._(MORE_BADGES_DESCRIPTOR, {count: hiddenCount})}
								onClick={(event) => openBadge(event, displayedBadges[0].key)}
								data-flx="user.user-profile-badges.more"
							>
								+{hiddenCount}
							</button>
						</FocusRing>
					</Tooltip>
				)}
			</div>
		);
	},
);
